/**
 * About page copy and media. Adapted for India from https://alearninglab.com/about-us/.
 * Keep SEO fields, H1 and intro here (owned by the SEO specialist), not in JSX.
 * Images live in /public/about (WebP). `file` is joined with PUBLIC_URL in the components.
 */

export const ABOUT_SEO = {
  title: "About Us | A Learning Lab India",
  description:
    "Where learning becomes experience. A Learning Lab helps students aged 13 to 18 in India try careers before they choose subjects or streams. Meet the team and partners behind it.",
  path: "/about",
};

export const ABOUT_HERO = {
  title: "Where learning becomes experience",
  highlight: "experience",
  /** Two lines on purpose: the second one always starts on its own line. */
  introLines: ["Less wondering. More discovering.", "Get a feel for the future before you choose it."],
};

export const ABOUT_GAP = {
  title: "A gap worth closing",
  paragraphs: [
    "Students are asked to choose subjects, streams and careers long before they have experienced what those paths involve. The result is uncertainty at best, and costly course corrections at worst.",
    "A Learning Lab was created to close that gap. We bring students aged 13 to 18 into direct contact with industries, practitioners and meaningful work, so that their choices are shaped by experience, not assumption.",
  ],
};

export const ABOUT_WAY = {
  title: "A Learning Lab way",
  pillars: [
    {
      id: "experience",
      title: "Experience first.",
      text: "Every programme is built around doing: researching, building, presenting and solving.",
    },
    {
      id: "practitioners",
      title: "Practitioners at the centre.",
      text: "Our mentors work in the fields they teach, and they share both the rewards and the realities.",
    },
    {
      id: "clarity",
      title: "Clarity over pressure.",
      text: "Finding out what you love is a win. So is finding out what you don't.",
    },
    {
      id: "outcomes",
      title: "Outcomes that stay.",
      text: "Projects, portfolios and skills students carry into university and beyond.",
    },
  ],
};

export const ABOUT_CLOSING = {
  line1: "We don't tell students who to become.",
  line2: "We give them the experiences to find out.",
  highlight: "experiences",
  cta: { label: "Explore our programmes", to: "/programs" },
};

export const FOUNDER = {
  eyebrow: "A message from our founder",
  photo: {
    file: "about/founder.webp",
    width: 900,
    height: 1200,
    alt: "Our founder speaking at a UniHawk event celebrating the class of 2025",
  },
  caption: "Our founder",
  lead: "Your future isn't a mystery.",
  main: "It's a choice waiting to be made.",
  paragraphs: [
    "Every confident adult started as a confused teenager who dared to explore beyond the classroom. Someone who believed that understanding themselves mattered more than following the crowd.",
    "You carry potential the world needs: your unique talents, your honest interests, your endless curiosity about what's possible.",
    "This is your time to discover who you're meant to become.",
  ],
  closing: "Your path isn't predetermined. Start walking it.",
};

export const ALLIANCES = {
  title: "Our strategic alliances",
  partners: [
    { id: "p01", file: "about/partner-01.webp", name: "Dubai American Academy" },
    { id: "p02", file: "about/partner-02.webp", name: "London Academy of Trading" },
    { id: "p03", file: "about/partner-03.webp", name: "Ensobrain" },
    { id: "p04", file: "about/partner-04.webp", name: "Beverley Morris & Co. Solicitors" },
    { id: "p05", file: "about/partner-05.webp", name: "Eddyfi Technologies" },
    { id: "p06", file: "about/partner-06.webp", name: "CyberSoph" },
    { id: "p07", file: "about/partner-07.webp", name: "abq" },
    { id: "p08", file: "about/partner-08.webp", name: "Partner school" },
    { id: "p09", file: "about/partner-09.webp", name: "GEMS Metropole School Al Waha" },
    { id: "p10", file: "about/partner-10.webp", name: "Innova International English School" },
    { id: "p11", file: "about/partner-11.webp", name: "The Academies by Harvard Student Agencies" },
    { id: "p12", file: "about/partner-12.webp", name: "Capitox Consulting Group" },
  ],
};

export const TEAM = {
  title: "The team behind A Learning Lab",
  members: [
    { id: "aneeqa", name: "Aneeqa Lareb", role: "Director", file: "about/aneeqa.webp", width: 264, height: 300 },
    { id: "yasmine", name: "Yasmine Mohamed Mukhawi", role: "Student Affairs Advisor", file: "about/yasmine.webp", width: 257, height: 300 },
    { id: "charavi", name: "Charavi Kumar", role: "Global Programmes Advisor", file: "about/charavi.webp", width: 560, height: 788 },
    { id: "tendekai", name: "Tendekai Lisbon Marufu", role: "Sales Associate", file: "about/tendekai.webp", width: 560, height: 560 },
  ],
};